import type {Unit} from 'src/internal/date-formatter/selectUnit';

const SECS_PER_DAY = 60 * 60 * 24;
const SECS_PER_WEEK = SECS_PER_DAY * 7;
const SECS_PER_QUARTER = SECS_PER_DAY * 91; // ~91 days per quarter
const SECS_PER_YEAR = SECS_PER_DAY * 365;

/**
 * Determines whether to use 'auto' or 'always' for the `numeric` option
 * in `Intl.RelativeTimeFormat`.
 *
 * When `numeric` is 'auto', values like 1 day, 1 week, 1 quarter, or 1 year
 * are formatted as calendar aliases ("tomorrow", "next week", "next quarter",
 * "next year"). These aliases imply the very next calendar unit, which is
 * misleading when rounding has caused the value to be 1 but the actual gap
 * exceeds that unit (e.g. 31 hours rounds to 1 day but is not "tomorrow").
 *
 * When the true gap exceeds 1 unit, we use 'always' instead, which produces
 * "in 1 day" / "1 day ago" rather than "tomorrow" / "yesterday".
 */
export default function getNumericOption(
  value: number,
  unit: Unit,
  rawSeconds: number,
): 'auto' | 'always' {
  // 'day', 'week', 'quarter', and 'year' have misleading calendar aliases when
  // the rounded value is ±1 but the actual gap exceeds 1 full unit.
  if (
    Math.abs(value) === 1 &&
    (unit === 'day' || unit === 'week' || unit === 'quarter' || unit === 'year')
  ) {
    const unitSecondsMap = {
      day: SECS_PER_DAY,
      week: SECS_PER_WEEK,
      quarter: SECS_PER_QUARTER,
      year: SECS_PER_YEAR,
    };
    const unitSeconds = unitSecondsMap[unit];
    // Suppress the calendar alias whenever the actual gap exceeds 1 full unit.
    if (Math.abs(rawSeconds) > unitSeconds) {
      return 'always';
    }
  }

  return 'auto';
}
